import { isEqual } from 'lodash-es';
import {
  requireWorkflowPayloadVersion,
  type WorkflowDefinitionAuthoringReferences,
  type WorkflowDefinitionDocument,
  type WorkflowStepProgressConfiguration,
} from './platform-workflow-definition-lifecycle.models';

export interface WorkflowStepProgressDraftValues {
  startMode: string;
  allowBypass: boolean;
  bypassCondition: string;
  outcomeCode: string;
  processDataKey: string;
  reason: string;
}
export type WorkflowStepProgressEditIntent = 'start-mode' | 'progress-policy';
export interface WorkflowStepProgressDraft {
  values: WorkflowStepProgressDraftValues;
  intent: WorkflowStepProgressEditIntent;
}
export interface WorkflowStepProgressAuthoringReferences {
  contracts: NonNullable<WorkflowDefinitionAuthoringReferences['routingContracts']>;
  loading: boolean;
}

function record(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** Saved unknown content can be inspected, but cannot be replaced by this editor. */
export function workflowStepProgressConfigurationIssue(value: unknown): string {
  if (value == null) return '';
  if (!record(value) || Object.keys(value).some(key => !['startMode', 'bypass'].includes(key))
    || typeof value['startMode'] !== 'string' || !['manual', 'assigned_open'].includes(value['startMode']))
    return 'Additional or unsupported saved step progress settings are preserved read-only.';
  const bypass = value['bypass'];
  if (bypass != null && (!record(bypass)
    || Object.keys(bypass).some(key => !['conditionCode', 'conditionVersion', 'outcomeCode', 'processDataKey', 'reason'].includes(key))
    || typeof bypass['conditionCode'] !== 'string' || !bypass['conditionCode'].trim()
    || !Number.isInteger(bypass['conditionVersion']) || Number(bypass['conditionVersion']) < 1
    || typeof bypass['outcomeCode'] !== 'string' || !bypass['outcomeCode'].trim() || typeof bypass['reason'] !== 'string'
    || bypass['processDataKey'] != null && typeof bypass['processDataKey'] !== 'string'))
    return 'Additional or unsupported saved bypass settings are preserved read-only.';
  return '';
}

/** The editor and its owner use the same narrow writer; this does not evaluate a bypass. */
export function applyWorkflowStepProgress(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  values: WorkflowStepProgressDraftValues,
  intent: WorkflowStepProgressEditIntent,
  references: WorkflowStepProgressAuthoringReferences,
): WorkflowDefinitionDocument {
  const nodes = Array.isArray(document.nodes) ? document.nodes.filter(node => record(node) && node.id === nodeId) : [];
  if (!nodeId || nodes.length !== 1 || nodes[0].typeCode !== 'human_step')
    throw new Error('Choose one unambiguous editable work step.');
  const node = nodes[0], saved = node.stepProgressConfiguration;
  const issue = workflowStepProgressConfigurationIssue(saved);
  if (issue) throw new Error(issue);
  if (!record(values) || Object.keys(values).some(key => !['startMode', 'allowBypass', 'bypassCondition', 'outcomeCode', 'processDataKey', 'reason'].includes(key))
    || typeof values.allowBypass !== 'boolean'
    || ['startMode', 'bypassCondition', 'outcomeCode', 'processDataKey', 'reason'].some(key => typeof values[key as keyof WorkflowStepProgressDraftValues] !== 'string')
    || intent !== 'start-mode' && intent !== 'progress-policy')
    throw new Error('Review step progress input.');
  if (values.startMode !== 'assigned_open' && values.startMode !== 'manual')
    throw new Error('Choose when work becomes In progress.');
  let configuration: WorkflowStepProgressConfiguration = { startMode: values.startMode };
  if (intent === 'start-mode') {
    configuration = { ...(saved == null ? {} : structuredClone(saved)), startMode: values.startMode };
  } else if (values.allowBypass) {
    // Count all matching identities before checking support; conflicting metadata is not a fallback.
    const matches = Array.isArray(references.contracts) ? references.contracts.filter(contract => record(contract)
      && typeof contract.code === 'string' && typeof contract.version === 'number'
      && `${contract.code}@${contract.version}` === values.bypassCondition) : [];
    const contract = matches.length === 1 ? matches[0] : null;
    if (references.loading || !contract || contract.supportsStepBypass !== true
      || typeof contract.code !== 'string' || !contract.code.trim() || !Number.isInteger(contract.version) || contract.version < 1
      || !Array.isArray(contract.outcomeCodes) || contract.outcomeCodes.some(code => typeof code !== 'string' || !code.trim())
      || new Set(contract.outcomeCodes).size !== contract.outcomeCodes.length || !contract.outcomeCodes.includes(values.outcomeCode))
      throw new Error('Choose an installed bypass condition and one of its supported results.');
    if (contract.code === 'workflow.process-flag' && values.outcomeCode !== 'bypass')
      throw new Error('This bypass condition requires the yes/no value to be Yes. Correct the saved result before applying.');
    const reason = values.reason.trim();
    if (!reason || reason.length > 240)
      throw new Error('Explain why this step can be bypassed in up to 240 characters.');
    if (contract.code === 'workflow.process-flag') {
      const fields = Array.isArray(document.processData?.fields) ? document.processData.fields.filter(field => record(field) && field.key === values.processDataKey) : [];
      if (fields.length !== 1 || fields[0].type !== 'boolean')
        throw new Error('Choose a yes/no process value for this bypass condition. Add one in Setup if needed.');
    }
    configuration.bypass = {
      conditionCode: contract.code, conditionVersion: contract.version, outcomeCode: values.outcomeCode, reason,
      ...(contract.code === 'workflow.process-flag' ? { processDataKey: values.processDataKey } : {}),
    };
  }
  if (saved && isEqual(configuration, saved)) configuration = saved;
  return {
    ...document,
    payloadSchemaVersion: requireWorkflowPayloadVersion(document.payloadSchemaVersion, '1.12'),
    nodes: document.nodes.map(item => item === node ? { ...item, stepProgressConfiguration: configuration } : item),
  };
}
